// Which of the two surfaces you are looking at.
//
// There are exactly two: the landing page, which is a form, and the stage,
// which is a canvas with the HUD over it. Everything that decides between them
// is one question asked once — whether this browser already has a body.

import type { VNode } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { DESIGN_H, DESIGN_W } from "../render/view";
import { fetchMe } from "../api";
import { startGame, type Game } from "../game";
import { loadSprites } from "../render/sprites";
import { Entering, Hud } from "./hud";
import { Landing } from "./landing";

type Session = { at: "checking" } | { at: "failed" } | { at: "landing" } | { at: "playing"; slug: string };

export function App({ siteKey }: { siteKey: string }): VNode {
  const [session, setSession] = useState<Session>({ at: "checking" });

  useEffect(() => {
    let mounted = true;
    void fetchMe().then((me) => {
      if (!mounted) return;
      if (!me) setSession({ at: "failed" });
      else setSession(me.user && me.character ? { at: "playing", slug: me.character.slug } : { at: "landing" });
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (session.at === "checking") {
    // A dark page with nothing on it, which is what it was before — but said
    // out loud, so a screen reader is not left in silence waiting for a route.
    return (
      <p class="visually-hidden" role="status">
        Loading.
      </p>
    );
  }
  if (session.at === "failed") {
    return (
      <main class="landing">
        <p role="alert">Could not load. Reload and try again.</p>
      </main>
    );
  }
  if (session.at === "landing") return <Landing siteKey={siteKey} />;
  return <Stage slug={session.slug} />;
}

/** The canvas and the HUD over it.
 *
 *  The two are siblings inside a box that shrink-wraps the canvas, so the HUD
 *  is clipped to the picture without anybody measuring anything: a panel can
 *  never end up hanging off the side of a world it belongs to. */
function Stage({ slug }: { slug: string }): VNode {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [game, setGame] = useState<Game | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    let started: Game | null = null;
    let dropped = false;
    setFailed(false);
    void loadSprites()
      .then(() => {
        if (dropped) return;
        started = startGame(el, slug);
        setGame(started);
      })
      .catch(() => {
        if (!dropped) setFailed(true);
      });
    return () => {
      dropped = true;
      started?.stop();
    };
  }, [slug]);

  return (
    <div class="stage">
      <div class="viewport">
        <canvas
          ref={canvas}
          class="screen"
          width={DESIGN_W * 2}
          height={DESIGN_H * 2}
          // Never in the tab order, but focusable, so closing the chat line has
          // somewhere to put the keyboard back.
          tabIndex={-1}
          role="img"
          aria-label="The fen. Everything the world can tell you is in the panels around it."
        />
        {game ? <Hud store={game.hud} commands={game.commands} /> : <Entering message={failed ? "could not load the game. reload and try again." : undefined} />}
      </div>
    </div>
  );
}
